import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Roles that should have admin access. Add more here if you introduce new ones.
const ADMIN_ROLES = ['admin', 'superadmin'];
const isAdminRole = (role) => ADMIN_ROLES.includes(role);

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Check customers collection first (web shoppers),
          // then fall back to users collection (POS employees / admin staff)
          let userData = null;
          let collection = 'customers';

          const custSnap = await getDoc(doc(db, 'customers', firebaseUser.uid));
          if (custSnap.exists()) {
            userData = custSnap.data();
          } else {
            const userSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (userSnap.exists()) {
              userData = userSnap.data();
              collection = 'users';
            }
          }

          const userInfo = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: userData?.name || firebaseUser.displayName || firebaseUser.email.split('@')[0],
            role: userData?.role || 'customer',
            phone: userData?.phone || '',
            address: userData?.address || '',
            createdAt: userData?.createdAt?.toDate() || firebaseUser.metadata.creationTime,
            emailVerified: firebaseUser.emailVerified,
            providerId: firebaseUser.providerData?.[0]?.providerId || 'password',
            isAdmin: isAdminRole(userData?.role),
            collection,
          };

          setCurrentUser(userInfo);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setCurrentUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
            role: 'customer',
            emailVerified: firebaseUser.emailVerified,
            providerId: firebaseUser.providerData?.[0]?.providerId || 'password',
            isAdmin: false,
            collection: 'customers',
          });
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = (userData) => {
    setCurrentUser(userData);
  };

  const register = (userData) => {
    setCurrentUser(userData);
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateUser = async (userData) => {
    try {
      setCurrentUser(prev => ({ 
        ...prev, 
        ...userData,
        isAdmin: isAdminRole(userData.role) || prev?.isAdmin || false
      }));
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const value = {
    currentUser,
    loading,
    isAdmin: currentUser?.isAdmin || false,
    login,
    register,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};